import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewContainerRef} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig} from '@angular/material/legacy-dialog';

import {Integration} from '@app/views/admin/application-settings/models/integration.model';
import {IntegrationTask} from '@app/views/admin/application-settings/models/integration-task.model';
import {copyData} from '@app/core/model/integration-models';
import {EntityFilterModel, FilterAction} from '@app/core/model/filtering/entity-filter-model';
import {Argument} from '@app/core/model/filtering/argument';
import {PagedData} from '@app/views/databrowsing/model/paged-data';
import {IEntity} from '@app/views/databrowsing/model/common.data.models';
import {Page} from '@app/views/databrowsing/model/page';
import {DATA_BROWSING_ENTITY_DETAIL_ROUTES} from '@app/views/databrowsing/model/entities-detail-routes.model';
import {DataBrowsingService} from '@app/views/databrowsing/databrowsing.service';
import {Person, SchoolModel} from '@app/views/databrowsing/model/rostering.models';
import {ApplicationSettingsService} from '@app/views/admin/application-settings/application-settings.service';
import {FormulaDialogComponent} from '@app/core/comp/dialog/formula-dialog/formula-dialog.component';

@Component({
    selector: 'ngc-entity-login-credentials',
    templateUrl: './entity-login-credentials.component.html',
    styleUrls: ['./entity-login-credentials.component.scss']
})
export class EntityLoginCredentialsComponent implements OnInit, OnDestroy {

    @Input() eduToneDS: string;
    @Input() otherDS: string;
    @Input() entityLoginCredentialsFormGroup: UntypedFormGroup;
    @Input() EMAIL: 'email';
    @Input() PASSWORD: 'password';
    @Input() defaultEmailOptions: { 'Student': string[], 'Staff': string[], 'SchoolAdmin': string[] };
    @Input() defaultPasswordOptions: { 'Student': string[], 'Staff': string[], 'SchoolAdmin': string[] };
    @Input() emailPredefinedTemplates: string[];
    @Input() providerGuid: string;
    @Input() integration: Integration;
    @Input() dataSourceConfigDomain: string;
    @Input() onSaveLoginCredentials$: Subject<void>;
    @Input() selectedOptionsObj: { 'email': string[], 'password': string[], 'domain': string } = {
        'email': [],
        'password': [],
        'domain': ''
    };

    @Input() set task(task: IntegrationTask) {
        this._task = task;
        this.addValidatorsToFormGroup();
    }

    get task(): IntegrationTask {
        return this._task;
    }

    @Input() set transformations(transformations: EntityFilterModel[]) {
        this._transformations = copyData(transformations);

        if (transformations?.length > 0) {
            this.transformation = transformations[0];
            this.transformation.customValues = ApplicationSettingsService.copyMap(transformations[0].customValues);
        } else {
            this.transformation = new EntityFilterModel(
                this.task.taskId,
                FilterAction.TRANSFORM,
            );
            this.transformation.customValues = new Map<string, Argument>();
        }

        this.addValidatorsToFormGroup();
    }

    get transformations(): EntityFilterModel[] {
        return this._transformations;
    }

    @Output() saveLoginCredentials = new EventEmitter<EntityFilterModel>();

    passwordPredefinedTemplates = [];
    availableEmailOptions = [];
    availablePasswordOptions = [];
    entityData: Person;
    school: SchoolModel;
    defaultExampleEntity = {
        'firstName': 'John',
        'lastName': 'Smith',
        'middleName': 'Jean',
        'graduationYear': '2023',
        'schoolNameInitial': 'NSDP',
        'primarySchoolName': 'National School District Preschool',
        'stateId': 'CA',
        'sisId': 'student1_faculty1',
        'birthday': '01012012',
        'domain': 'domain.com'
    };
    templateResultsObj = {'email': '', 'password': '', 'domain': ''};
    hidePasswordExample = true;
    transformation: EntityFilterModel;

    readonly USERNAME_MAY_CONTAIN_CHARACTERS_MESSAGE = 'Email/username may contain any of the following: letters (a-z), numbers (0-9), dashes(-), underscores(_), apostrophes(\'), and periods (.).';
    readonly USERNAME_CONTAIN_NOT_ALLOWED_CHARACTERS_MESSAGE = 'Email/username may not contain any of the following: ampersands(&), equal signs (=), brackets (< >), plus signs (+), commas(,).';
    readonly USERNAME_DOUBLE_PERIODS_MESSAGE = 'Email/username may not contain more than one period (.) in a row.';
    readonly USERNAME_BEGIN_END_PERIOD_MESSAGE = 'Email/username may not begin or end with a period (.)';
    readonly FIELD_IS_REQUIRED_MESSAGE = 'Field is required';

    private unsubscribe$ = new Subject<void>();
    private _transformations: EntityFilterModel[];
    private _task: IntegrationTask;

    constructor(private formBuilder: UntypedFormBuilder,
                private dataBrowsingService: DataBrowsingService,
                public viewContainerRef: ViewContainerRef,
                public matDialog: MatDialog) {
    }

    ngOnInit(): void {
        const emailAttributes = this.defaultEmailOptions[this.task[`${this.eduToneDS}Type`]].slice();
        const passwordAttributes = this.defaultPasswordOptions[this.task[`${this.eduToneDS}Type`]].slice();

        this.availableEmailOptions = this.getAvailableOptions(
            this.selectedOptionsObj.email, emailAttributes, this.emailPredefinedTemplates);
        this.availablePasswordOptions = this.getAvailableOptions(
            this.selectedOptionsObj.password, passwordAttributes, this.passwordPredefinedTemplates);

        this.getEntityItem();
        this.getTemplateResults();
        this.saveLoginCredentialsSubscription();
    }

    private getAvailableOptions(selectedOptions: string[], defaultOptions: string[], predefinedTemplates: string[]): string[] {
        return predefinedTemplates.concat(defaultOptions);
    }

    private addValidatorsToFormGroup(): void {
        if (this.transformations) {
            this.entityLoginCredentialsFormGroup.controls.email.setValidators([
                Validators.required,
                this.usernameContainOnlyAllowedCharactersValidator.bind(this),
                this.containNotAllowedCharactersValidator.bind(this),
                this.containDoublePeriodsValidator.bind(this),
                this.beginOrEndWithPeriodValidator.bind(this),
            ]);
        }
    }

    private usernameContainOnlyAllowedCharactersValidator(control: UntypedFormControl): { [s: string]: boolean } {
        let result: {};
        if (this.selectedOptionsObj?.email?.length > 0) {
            this.selectedOptionsObj.email.forEach((email: string) => {
                const value = ((email[0] === '{') && (email[email.length - 1] === '}')) ? email.slice(1, -1) : email;
                const usernameContainOnlyAllowedCharactersRegex = new RegExp('^[a-zA-Z0-9\-_\'\.]');
                const isFieldNotEmpty = value || (this.selectedOptionsObj.email.length > 0);

                result = result
                || (isFieldNotEmpty && !usernameContainOnlyAllowedCharactersRegex.test(value))
                    ? {'usernameContainOnlyAllowedCharactersError': true}
                    : null;
            });
        }
        return result;
    }

    private containNotAllowedCharactersValidator(control: UntypedFormControl): { [s: string]: boolean } {
        let result: {};
        if (this.selectedOptionsObj?.email?.length > 0) {
            this.selectedOptionsObj.email.forEach((email: string) => {
                const value = ((email[0] === '{') && (email[email.length - 1] === '}')) ? email.slice(1, -1) : email;
                const containNotAllowedCharactersRegex = new RegExp('^[&=<>+,]');
                const isFieldNotEmpty = value || (this.selectedOptionsObj.email.length > 0);

                result = result
                || (isFieldNotEmpty && containNotAllowedCharactersRegex.test(value))
                    ? {'containNotAllowedCharactersError': true}
                    : null;
            });
        }
        return result;
    }

    private containDoublePeriodsValidator(control: UntypedFormControl): { [s: string]: boolean } {
        let result: {};
        if (this.selectedOptionsObj?.email?.length > 0) {
            const value = (this.selectedOptionsObj.email.join()) || '';
            const doublePeriods = value.split('..').includes('..');
            const isFieldNotEmpty = value || (this.selectedOptionsObj.email.length > 0);

            result = isFieldNotEmpty && doublePeriods
                ? {'containDoublePeriodsError': true}
                : null;

        }
        return result;
    }

    private beginOrEndWithPeriodValidator(control: UntypedFormControl): { [s: string]: boolean } {
        let result: {};
        if (this.selectedOptionsObj?.email?.length > 0) {
            const value = (this.selectedOptionsObj.email.join()) || '';
            const beginOrEndWithPeriod = (value[0] === '.') || (value[value.length - 1] === '.');
            const isFieldNotEmpty = value || (this.selectedOptionsObj.email.length > 0);

            result = (isFieldNotEmpty && beginOrEndWithPeriod)
                ? {'beginOrEndWithPeriodError': true}
                : null;
        }
        return result;
    }

    private getEntityItem(): any {
        const page = this.setParamsRequest();

        return this.dataBrowsingService.getProviderData(page).pipe(takeUntil(this.unsubscribe$))
            .subscribe((pagedData: PagedData<IEntity>) => {
                if (pagedData?.data[0]?.data) {
                    this.entityData = <Person>pagedData.data[0].data;
                    this.getTemplateResults();
                }
            });
    }

    private setParamsRequest(): Page {
        const page = new Page();
        page.entity = DATA_BROWSING_ENTITY_DETAIL_ROUTES[this.task[`${this.eduToneDS}Type`]];
        page.orgGuid = this.providerGuid;
        page.limit = 1;
        (<any>page).page = 1;
        return page;
    }

    onChangeSelectedEmailOptions(selectedItems: string[]) {
        const lastItem = selectedItems[selectedItems.length - 1];

        const emailAttributes = this.defaultEmailOptions[this.task[`${this.eduToneDS}Type`]].slice();
        const selectedAttribute = this.isAttribute(lastItem, emailAttributes);

        if (selectedAttribute) {
            selectedItems.pop();
            this.openFormulaDialog(
                {item: selectedAttribute, selectedItems: selectedItems, index: selectedItems.length}, this.EMAIL
            );

        } else {

            this.selectedOptionsObj.email = this.getDividedTemplateSelectedItemsIfItWasSelected(
                selectedItems, this.emailPredefinedTemplates, emailAttributes, this.EMAIL);
            this.availableEmailOptions = this.getAvailableOptions(
                this.selectedOptionsObj.email, emailAttributes, this.emailPredefinedTemplates);
            // ToDo: add changes to this method
            this.getEmailTemplateResult();
        }
    }

    private isAttribute(item: string, attributes: string[]) {
        return attributes.find((attribute: string) =>
            (attribute.toLowerCase() === item.toLowerCase())
            || (attribute.toLowerCase() === `{${item}}`.toLowerCase())
        );
    }

    onChangeSelectedPasswordOptions(selectedItems: string[]) {
        const lastItem = selectedItems[selectedItems.length - 1];

        const passwordAttributes = this.defaultPasswordOptions[this.task[`${this.eduToneDS}Type`]].slice();
        const selectedAttribute = this.isAttribute(lastItem, passwordAttributes);

        if (selectedAttribute) {
            selectedItems.pop();
            this.openFormulaDialog(
                {item: selectedAttribute, selectedItems: selectedItems, index: selectedItems.length}, this.PASSWORD
            );

        } else {

            this.selectedOptionsObj.password = this.getDividedTemplateSelectedItemsIfItWasSelected(
                selectedItems, this.passwordPredefinedTemplates, passwordAttributes, this.PASSWORD);
            this.availablePasswordOptions = this.getAvailableOptions(
                this.selectedOptionsObj.password, passwordAttributes, this.passwordPredefinedTemplates);
            // ToDo: add changes to this method
            this.getPasswordTemplateResult();
        }
    }

    onChangeDomain(domain: string) {
        this.selectedOptionsObj.domain = domain;
        this.getDomainTemplateResult();
    }

    private getDividedTemplateSelectedItemsIfItWasSelected(
        selectedItems: string[], predefinedTemplates: string[], attributesList: string[], selectedFieldName: 'email' | 'password'
    ) {
        // if not only {attribute} was selected
        if (selectedItems.length > 0) {

            const lastItem = selectedItems[selectedItems.length - 1];
            const typedPredefinedTemplates = predefinedTemplates.find((template: string) =>
                template.toLowerCase() === lastItem.toLowerCase());
            if (typedPredefinedTemplates) {
                const newlyAddedTemplateElements = ApplicationSettingsService.splitLoginCredentialsPatternTemplateIntoSlices(
                    typedPredefinedTemplates, attributesList);
                selectedItems.pop();
                selectedItems = selectedItems.concat(newlyAddedTemplateElements);
            } else {

                const openParenthesesIndexOf = lastItem.indexOf('(');
                const possibleFormulaTitle = (openParenthesesIndexOf > -1) ? lastItem.slice(0, openParenthesesIndexOf) : lastItem;

                const formulaOperation = FormulaOperations.find((formula: LoginCredentialsFormula) =>
                    (formula.title.toLowerCase() === possibleFormulaTitle.toLowerCase()));

                if (formulaOperation
                    && ((lastItem === formulaOperation.title)
                        || (lastItem === `{${formulaOperation.title}}`)
                        || (lastItem === `{${formulaOperation.title}()}`)
                        || (lastItem === `${formulaOperation.title}()`))) {
                    selectedItems.pop();
                    this.openFormulaDialog({
                        item: `{${formulaOperation.title}()}`,
                        selectedItems: selectedItems, index: selectedItems.length
                    }, selectedFieldName);
                }

                // ToDo: check if constant were typed
                // ToDo: check if string with formulas/attributes/constant were typed/pasted and split it into slices
            }
        }
        return selectedItems;
    }

    private getTemplateResults() {
        this.getEmailTemplateResult();
        this.getPasswordTemplateResult();
        this.getDomainTemplateResult();
    }

    getEmailTemplateResult() {
        // if (this.selectedOptionsObj.email.length === 3) {
        this.templateResultsObj.email = `${this.getFirstName()}.${this.getLastName()}@${this.getDomain()}`;
        // }
    }

    getFirstName() {
        return this.entityData?.name?.givenName || this.defaultExampleEntity.firstName;
    }

    getLastName() {
        return this.entityData?.name?.familyName || this.defaultExampleEntity.lastName;
    }

    // getMiddleName() {
    //     return this.entityData?.name?.middleName;
    // }
    // getGraduationYear() {
    //     return (<any>this.entityData)?.schoolYear;
    // }
    // schoolNameInitial() {
    //     return this.school?.schoolName;
    // }

    getStateId() {
        return this.school?.stateId || this.defaultExampleEntity.stateId;
    }

    getBirthday() {
        return this.entityData?.demographic?.birthDate || this.defaultExampleEntity.birthday;
    }

    getPasswordTemplateResult() {
        // if (this.selectedOptionsObj.password.length === 2) {
        this.templateResultsObj.password = `${this.getStateId()}${this.getBirthday()}`;
        // }
    }

    getDomainTemplateResult() {
        this.templateResultsObj.domain = `${this.getFirstName()}.${this.getLastName()}@${this.getDomain()}`;
    }

    getDomain() {
        return this.dataSourceConfigDomain || this.defaultExampleEntity.domain;
    }

    private saveLoginCredentialsSubscription() {
        if (this.onSaveLoginCredentials$) {
            this.onSaveLoginCredentials$.pipe(takeUntil(this.unsubscribe$))
                .subscribe(() => {
                    if (!this.transformation.customValues) {
                        this.transformation.customValues = new Map<string, Argument>();
                    }

                    const email = this.entityLoginCredentialsFormGroup.controls.email.value.join('');
                    const domain = this.entityLoginCredentialsFormGroup.controls.domain.value;
                    const password = this.entityLoginCredentialsFormGroup.controls.password.value.join('');

                    this.transformation.customValues.set('credentials.username', new Argument(email, email));
                    this.transformation.customValues.set('domain', new Argument(domain, domain));
                    this.transformation.customValues.set('credentials.password', new Argument(password, password));
                    this.saveLoginCredentials.next(this.transformation);
                });
        }
    }

    onEditChip(payload: { item: any, selectedItems: any[], index: number }, selectedFieldName: 'email' | 'password') {
        this.openFormulaDialog(payload, selectedFieldName);
    }

    private openFormulaDialog(
        {item, selectedItems, index}: { item: any, selectedItems: any[], index: number }, selectedFieldName: 'email' | 'password'
    ): void {
        const config = new MatDialogConfig();
        config.viewContainerRef = this.viewContainerRef;
        config.autoFocus = false;
        config.width = '800px';
        const dialogRef = this.matDialog.open(FormulaDialogComponent, config);

        const emailAttributes = this.defaultEmailOptions[this.task[`${this.eduToneDS}Type`]].slice();
        const passwordAttributes = this.defaultPasswordOptions[this.task[`${this.eduToneDS}Type`]].slice();

        const attributesList = (selectedFieldName === this.EMAIL) ? emailAttributes : passwordAttributes;
        if (item.startsWith('{') && item.endsWith('}')) {
            item = item.slice(1, -1);
        }

        const [attribute, formulaWithArgumentsInParentheses, formulaTitle, argumentsList] =
            ApplicationSettingsService.getParsedFormulaData(item, attributesList);
        let attributeName: string;
        let selectedFormulaArguments: string[];

        if (formulaTitle) {
            attributeName = argumentsList[0];
            selectedFormulaArguments = argumentsList.splice(1);
        }

        dialogRef.componentInstance.item = item;
        dialogRef.componentInstance.formulaOperations = FormulaOperations.slice();
        dialogRef.componentInstance.attributes = attributesList;
        dialogRef.componentInstance.selectedFormulaName = formulaTitle;
        dialogRef.componentInstance.selectedAttributeName = attribute || attributeName;
        dialogRef.componentInstance.selectedFormulaArguments = selectedFormulaArguments;

        dialogRef.afterClosed().subscribe((result: string | undefined) => {
            if (result) {
                this.selectedOptionsObj[selectedFieldName][index] = result;
                this.entityLoginCredentialsFormGroup.controls[selectedFieldName].setValue(this.selectedOptionsObj[selectedFieldName]);
                this.entityLoginCredentialsFormGroup.controls[selectedFieldName].markAsDirty();
            }
        });
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}


export enum LOGIN_CREDENTIALS_FORMULA_TYPES {
    NUMBER = 'number',
    TEXT = 'text'
}

export const FormulaOperations: LoginCredentialsFormula[] = [
    {
        title: 'firstChars',
        argumentsList: [
            {type: LOGIN_CREDENTIALS_FORMULA_TYPES.NUMBER, argumentFieldName: 'Characters quantity'},
        ]
    },
    {
        title: 'lastChars',
        argumentsList: [
            {type: LOGIN_CREDENTIALS_FORMULA_TYPES.NUMBER, argumentFieldName: 'Characters quantity'},
        ]
    },
    {
        title: 'substr',
        argumentsList: [
            {type: LOGIN_CREDENTIALS_FORMULA_TYPES.NUMBER, argumentFieldName: 'Start'},
            {type: LOGIN_CREDENTIALS_FORMULA_TYPES.NUMBER, argumentFieldName: 'Length'}
        ]
    },
    {
        title: 'textBefore',
        argumentsList: [{type: LOGIN_CREDENTIALS_FORMULA_TYPES.TEXT, argumentFieldName: 'Character', maxlength: 1}]
    },
    {
        title: 'initial',
        argumentsList: []
    },
    {
        title: 'firstWord',
        argumentsList: []
    }
];

export class LoginCredentialsFormula {
    title: string;
    argumentsList: LoginCredentialsFormulaArgument[];
}

export class LoginCredentialsFormulaArgument {
    type: LOGIN_CREDENTIALS_FORMULA_TYPES;
    argumentFieldName: string;
    maxlength?: number;
}

